import React, { useCallback, useEffect, useRef, useState } from "react";
import { Alert, Box, Step, StepLabel, Stepper } from "@mui/material";
import ConfirmDialog from "../../../components/ConfirmDialog/ConfirmDialog";
import dialogStyles from "../../../components/ConfirmDialog/ConfirmDialog.module.css";
import ColorPalette from "../../../colorpalette/ColorPalette";
import { DeletePatientAPI, GetPatientDeletePreviewAPI } from "../../../api/API";
import usePatientExport from "./usePatientExport";
import { DeleteConfirmStep, DeleteExportStep, DeletePatientIdentity, DeletePreviewStep } from "./PatientDeleteSteps";

const STEP_LABELS = ["Resumo", "Exportar", "Confirmar"];

// Mesmo padrão do ProgressStepper da agenda: cinza por padrão, azul no ativo e nos concluídos.
const DIALOG_MAX_WIDTH = 720;
const TOP_ROW_SX = {
    display: "flex",
    flexDirection: { xs: "column", sm: "row" },
    alignItems: { xs: "stretch", sm: "center" },
    gap: { xs: "16px", sm: "24px" },
    marginTop: "16px", // respiro entre o cabeçalho vermelho e o conteúdo
    marginBottom: "16px",
};
const STEPPER_SX = {
    padding: 0,
    flex: 1,
    minWidth: 0,
    "& .MuiStepConnector-line": { borderColor: ColorPalette.border, borderTopWidth: 1.5 },
    "& .MuiStepConnector-root.Mui-active .MuiStepConnector-line": { borderColor: ColorPalette.blueMain },
    "& .MuiStepConnector-root.Mui-completed .MuiStepConnector-line": { borderColor: ColorPalette.blueMain },
    "& .MuiStepIcon-root": { color: ColorPalette.blueLight3, fontSize: 24 },
    "& .MuiStepIcon-root.Mui-active": { color: ColorPalette.blueMain },
    "& .MuiStepIcon-root.Mui-completed": { color: ColorPalette.blueMain },
    "& .MuiStepLabel-label": { color: ColorPalette.textLight, fontSize: "0.8125rem" },
    "& .MuiStepLabel-label.MuiStepLabel-alternativeLabel": { marginTop: "6px" },
    "& .MuiStepLabel-label.Mui-active": { color: ColorPalette.blueMain, fontWeight: 600 },
    "& .MuiStepLabel-label.Mui-completed": { color: ColorPalette.textDark },
};
const DANGER_COLOR = "#dc2626"; // mesmo vermelho do variant danger do ConfirmDialog
const DEFAULT_ERROR = "Não foi possível excluir este paciente.";
const TIMEOUT_ERROR = "Não foi possível confirmar a exclusão: a conexão falhou ou demorou demais. "
    + "A ficha foi recarregada; se o paciente continuar aqui, tente novamente.";

// Mensagem legível de um erro da API ({ error, message }); `error` pode ser só o código.
function errorMessage(error, fallback) {
    if (error?.message) return error.message;
    const code = error?.error;
    if (typeof code === "string" && !/^[A-Z_]+$/.test(code)) return code;
    return fallback;
}

/**
 * Diálogo "Excluir paciente" (LGPD, ficha lápide) em três passos:
 * 1. resumo do que será apagado e do que fica (prévia do backend, com avisos e bloqueios);
 * 2. exportação obrigatória do prontuário, que passa à guarda da clínica;
 * 3. confirmação com dois aceites e o nome exato do paciente.
 * Aberto pelo painel "Privacidade e dados" (só o dono da clínica); fica sempre
 * montado, só o `open` alterna. Erros aparecem dentro do diálogo: o Toast fica
 * atrás da sobreposição do ConfirmDialog. O Toast de sucesso é da página
 * (Patient.js), porque a ficha vira lápide e o painel é desmontado.
 * Especificação: docs/features/lgpd-eliminacao/README.md
 */
function PatientDeleteDialog({ patientId, patientName, open, onClose, onDone, onSwitchToAnonymize }) {
    const [step, setStep] = useState(1);
    const [preview, setPreview] = useState(null);
    const [previewLoading, setPreviewLoading] = useState(false);
    const [previewError, setPreviewError] = useState(null);
    const [sessionExported, setSessionExported] = useState(false);
    const [custodyChecked, setCustodyChecked] = useState(false);
    const [irreversibleChecked, setIrreversibleChecked] = useState(false);
    const [typedName, setTypedName] = useState("");
    const [deleting, setDeleting] = useState(false);
    const [error, setError] = useState(null);
    const requestRef = useRef(0);
    const { exporting, exportNow } = usePatientExport(patientId);

    const loadPreview = useCallback(() => {
        const requestId = ++requestRef.current;
        setPreviewLoading(true);
        setPreviewError(null);

        const finish = (data, failure) => {
            if (requestId !== requestRef.current) return;
            setPreviewLoading(false);
            setPreview(failure ? null : data);
            setPreviewError(failure ? errorMessage(data, "Não foi possível carregar o resumo da exclusão.") : null);
        };

        GetPatientDeletePreviewAPI(patientId, (data) => finish(data, false), (data) => finish(data, true));
    }, [patientId]);

    // Abrir carrega a prévia; fechar (por qualquer caminho) zera o diálogo.
    useEffect(() => {
        if (open) {
            loadPreview();
            return;
        }
        requestRef.current++;
        setStep(1);
        setPreview(null);
        setPreviewLoading(false);
        setPreviewError(null);
        setSessionExported(false);
        setCustodyChecked(false);
        setIrreversibleChecked(false);
        setTypedName("");
        setDeleting(false);
        setError(null);
    }, [open, loadPreview]);

    const busy = exporting || deleting;
    const blockers = Array.isArray(preview?.blockers) ? preview.blockers : [];
    const previewAllowsDelete = !!preview && preview.canDelete !== false && !preview.alreadyDeleted && blockers.length === 0;
    const nameMatches = typedName.trim() === (patientName || "").trim();
    const canDelete = previewAllowsDelete && sessionExported && custodyChecked && irreversibleChecked && nameMatches && !busy;
    const canAdvance = step === 1
        ? !previewLoading && previewAllowsDelete
        : sessionExported && !exporting;

    const handleCancel = () => {
        if (busy) return;
        onClose();
    };

    const goTo = (nextStep) => {
        setError(null);
        setStep(nextStep);
    };

    const handleSwitchToAnonymize = () => {
        if (busy) return;
        onClose();
        if (onSwitchToAnonymize) onSwitchToAnonymize();
    };

    const handleExport = async () => {
        setError(null);
        const result = await exportNow();
        if (result.success) {
            setSessionExported(true);
        } else {
            setError(result.error);
        }
    };

    const handleDeleteError = (response, statusCode) => {
        setDeleting(false);
        const code = response?.error;

        if (code === "ALREADY_DELETED") {
            onClose();
            if (onDone) onDone();
            return;
        }
        if (code === "BLOCKED") {
            setError(errorMessage(response, DEFAULT_ERROR));
            setStep(1);
            loadPreview();
            return;
        }
        if (!statusCode) {
            // Sem status HTTP: rede ou tempo limite do CallPOSTAPI. A exclusão pode
            // ter sido concluída no servidor, então a ficha é recarregada.
            setError(TIMEOUT_ERROR);
            if (onDone) onDone();
            return;
        }
        setError(errorMessage(response, DEFAULT_ERROR));
    };

    const handleDelete = () => {
        if (!canDelete) return;
        setDeleting(true);
        setError(null);

        DeletePatientAPI(
            patientId,
            (response) => {
                if (!response || response.success === false || response.error) {
                    handleDeleteError(response, 200);
                    return;
                }
                setDeleting(false);
                onClose();
                if (onDone) onDone(response);
            },
            handleDeleteError
        );
    };

    return (
        <ConfirmDialog
            isOpen={open}
            variant="danger"
            title="Excluir paciente"
            onCancel={handleCancel}
            hideFooter
            maxWidth={DIALOG_MAX_WIDTH}
        >
            {/* No desktop a identificação e o stepper dividem uma linha; no celular empilham. */}
            <Box sx={TOP_ROW_SX}>
                <Box sx={{ flex: { sm: "0 1 280px" }, minWidth: 0 }}>
                    <DeletePatientIdentity patientId={patientId} patientName={patientName} />
                </Box>

                <Stepper activeStep={step - 1} alternativeLabel sx={STEPPER_SX}>
                    {STEP_LABELS.map((label) => (
                        <Step key={label}>
                            <StepLabel>{label}</StepLabel>
                        </Step>
                    ))}
                </Stepper>
            </Box>

            <Box sx={{ maxHeight: "calc(100vh - 340px)", minHeight: "120px", overflowY: "auto", overflowX: "hidden" }}>
                {step === 1 && (
                    <DeletePreviewStep
                        patientId={patientId}
                        loading={previewLoading}
                        loadError={previewError}
                        preview={preview}
                        onRetry={loadPreview}
                        onSwitchToAnonymize={handleSwitchToAnonymize}
                    />
                )}
                {step === 2 && (
                    <DeleteExportStep exporting={exporting} exported={sessionExported} onExport={handleExport} />
                )}
                {step === 3 && (
                    <DeleteConfirmStep
                        patientName={patientName}
                        custodyChecked={custodyChecked}
                        irreversibleChecked={irreversibleChecked}
                        typedName={typedName}
                        disabled={deleting}
                        onCustodyChange={setCustodyChecked}
                        onIrreversibleChange={setIrreversibleChecked}
                        onTypedNameChange={setTypedName}
                    />
                )}

                {error && <Alert severity="error" sx={{ marginTop: 2 }}>{error}</Alert>}
            </Box>

            <div className={dialogStyles.footer} style={{ margin: "16px -20px -16px" }}>
                {step > 1 && (
                    <button
                        type="button"
                        className={dialogStyles.btnCancel}
                        style={{ marginRight: "auto" }}
                        onClick={() => goTo(step - 1)}
                        disabled={busy}
                    >
                        Voltar
                    </button>
                )}
                <button type="button" className={dialogStyles.btnCancel} onClick={handleCancel} disabled={busy}>
                    Cancelar
                </button>
                {step < 3 ? (
                    <button
                        type="button"
                        className={dialogStyles.btnConfirm}
                        style={{ background: ColorPalette.blueMain }}
                        onClick={() => goTo(step + 1)}
                        disabled={!canAdvance}
                    >
                        Avançar
                    </button>
                ) : (
                    <button
                        type="button"
                        className={dialogStyles.btnConfirm}
                        style={{ background: DANGER_COLOR }}
                        onClick={handleDelete}
                        disabled={!canDelete}
                    >
                        {deleting && <span className={dialogStyles.spinner} />}
                        {deleting ? "Aguarde..." : "Excluir definitivamente"}
                    </button>
                )}
            </div>
        </ConfirmDialog>
    );
}

export default PatientDeleteDialog;
